// Moved out of pages/CanvasUI.tsx unchanged (Threads split).
import React, { useState, useRef, useEffect } from 'react';
import { Activity, ChevronDown, ChevronLeft, Sparkles, X, Check, ChevronRight, Loader2, Edit, Download, Eye, ChevronUp, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { CheckCircle2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import type { PendingActionErrorState, ReplayRecord } from './canvasShared';

export const PendingActionBubble: React.FC<{
  actions: Array<{ id: number; summary: string; status?: string | null }>;
  onDecision: (actionId: number, decision: 'approve' | 'cancel') => void;
  isActionProcessing: (actionId: number) => boolean;
  failedActions?: Record<number, PendingActionErrorState>;
}> = ({
  actions,
  onDecision,
  isActionProcessing,
  failedActions = {},
}) => {
  return (
    <div className="mt-3 rounded-lg border border-dashed border-orange-200 bg-orange-50 p-3 text-sm">
      <p className="text-xs uppercase text-orange-700 mb-2">Approval required</p>
      <div className="space-y-2">
        {actions.map((action) => {
          const rawStatus = (action.status ?? 'pending').toString();
          const normalizedStatus = rawStatus.toLowerCase();
          const isPending = normalizedStatus === 'pending';
          const processing = isActionProcessing(action.id);
          const errorInfo = failedActions[action.id];
          const containerClasses = [
            'flex flex-wrap items-start justify-between gap-2 rounded-md px-3 py-2 border',
            errorInfo ? 'border-red-300 bg-red-50/80' : 'border-transparent bg-white/60',
          ].join(' ');
          return (
            <div
              key={action.id}
              className={containerClasses}
            >
              <div>
                <p className="font-medium text-orange-900">{action.summary}</p>
                <p
                  className={`text-xs capitalize ${
                    errorInfo ? 'text-red-700 font-semibold' : 'text-orange-600'
                  }`}
                >
                  {errorInfo ? 'Approval failed' : rawStatus}
                </p>
              </div>
              {isPending ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onDecision(action.id, 'cancel')}
                    disabled={processing}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => onDecision(action.id, 'approve')}
                    disabled={processing}
                  >
                    Approve
                  </Button>
                </div>
              ) : (
                <span className="text-xs text-orange-700 capitalize">{rawStatus}</span>
              )}
              {errorInfo && (
                <div className="w-full flex items-start gap-2 text-xs text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{errorInfo.message}</p>
                    {errorInfo.correlationId && (
                      <p className="font-mono text-[11px] uppercase tracking-wide opacity-80">
                        Reference ID: {errorInfo.correlationId}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Activity Stream Component - COMMENTED OUT FOR LATER
// const ActivityStream: React.FC<{
//   isOpen: boolean;
//   onClose: () => void;
//   activities: ActivityItem[];
// }> = ({ isOpen, onClose, activities }) => {
//   if (!isOpen) return null;
//
//   return (
//     <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-xl border-l border-gray-200 z-40 transform transition-transform duration-300">
//       <div className="h-full flex flex-col">
//         <div className="flex items-center justify-between p-4 border-b border-gray-200">
//           <h2 className="text-lg font-semibold text-gray-900">Activity Stream</h2>
//           <button
//             onClick={onClose}
//             className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
//           >
//             <X className="w-5 h-5" />
//           </button>
//         </div>
//         
//         <div className="flex-1 overflow-y-auto p-4 space-y-4">
//           {activities.length === 0 ? (
//             <div className="text-center py-8 text-gray-500">
//               <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
//               <p>No recent activity</p>
//             </div>
//           ) : (
//             activities.map((activity) => (
//               <div key={activity.id} className="bg-gray-50 rounded-lg p-4">
//                 <div className="flex items-start justify-between mb-2">
//                   <h3 className="font-medium text-gray-900">{activity.title}</h3>
//                   <span className="text-xs text-gray-500">
//                     {activity.timestamp.toLocaleTimeString()}
//                   </span>
//                 </div>
//                 <p className="text-sm text-gray-600 mb-3">{activity.description}</p>
//                 
//                 {activity.actions && (
//                   <div className="flex space-x-2">
//                     {activity.actions.map((action, index) => (
//                       <button
//                         key={index}
//                         className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
//                           action.type === 'primary'
//                             ? 'bg-orange-600 text-white hover:bg-orange-700'
//                             : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//                         }`}
//                       >
//                         {action.label}
//                       </button>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             ))
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// Custom markdown components
export const MarkdownComponents = {
  code({ node, inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '');
    return !inline && match ? (
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    ) : (
      <code className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded font-mono text-sm" {...props}>
        {children}
      </code>
    );
  },
};

// Function to process message content and render CRM components
export const processMessageContent = (content: string) => {
  // First, remove source citations from the content
  let cleanedContent = content;

  // Remove source citations in various formats, but preserve markdown links
  cleanedContent = cleanedContent.replace(/\[\d+\]/g, ''); // Remove [1], [2], etc.
  cleanedContent = cleanedContent.replace(/\(Source: [^)]+\)/g, ''); // Remove (Source: ...)
  cleanedContent = cleanedContent.replace(/Source: https?:\/\/[^\s\n]+/g, ''); // Remove standalone source URLs
  // Note: Removed the line that strips all URLs to preserve markdown links

  // Remove SOURCES sections and bullet points
  cleanedContent = cleanedContent.replace(/SOURCES?:\s*\*?/gi, ''); // Remove "SOURCES:" or "SOURCE:" headers
  cleanedContent = cleanedContent.replace(/^\s*\*\s*$/gm, ''); // Remove lines with just asterisks
  cleanedContent = cleanedContent.replace(/^\s*\*\s*(?=\s*$)/gm, ''); // Remove trailing asterisks

  // Clean up extra whitespace and newlines
  cleanedContent = cleanedContent.replace(/\n\s*\n\s*\n/g, '\n\n');
  cleanedContent = cleanedContent.trim();

  // Check for CRM component tags
  const crmTagPattern = /<(contact-card|contact-list|trip-card|trip-list)\s*data='([^']+)'><\/\1>/g;
  
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = crmTagPattern.exec(cleanedContent)) !== null) {
    // Add text before the tag
    if (match.index > lastIndex) {
      const textBefore = cleanedContent.slice(lastIndex, match.index);
      if (textBefore.trim()) {
        parts.push({
          type: 'text',
          content: textBefore,
          key: `text-${lastIndex}`
        });
      }
    }

    // Add the CRM component
    const [fullMatch, tagName, dataString] = match;
    try {
      const data = JSON.parse(dataString);
      parts.push({
        type: 'component',
        componentType: tagName,
        data: data,
        key: `component-${match.index}`
      });
    } catch (error) {
      console.error('❌ Error parsing CRM component data:', error);
      // If parsing fails, treat as regular text
      parts.push({
        type: 'text',
        content: fullMatch,
        key: `error-${match.index}`
      });
    }

    lastIndex = match.index + fullMatch.length;
  }

  // Add remaining text
  if (lastIndex < cleanedContent.length) {
    const remainingText = cleanedContent.slice(lastIndex);
    if (remainingText.trim()) {
      parts.push({
        type: 'text',
        content: remainingText,
        key: `text-${lastIndex}`
      });
    }
  }

  // If no CRM components found, return the cleaned content as text
  if (parts.length === 0) {
    return [{
      type: 'text',
      content: cleanedContent,
      key: 'text-0'
    }];
  }

  return parts;
};

// Component to render processed message parts
export const MessageContent: React.FC<{ content: string }> = ({ content }) => {
  const parts = processMessageContent(content);

  return (
    <div>
      {parts.map((part) => {
        if (part.type === 'component') {
          // Handle CRM components if needed
          return null;
        } else {
          // Render text content with markdown
          return (
            <div key={part.key} className="prose max-w-none text-foreground break-words overflow-wrap-anywhere dark:prose-invert">
              <ReactMarkdown components={MarkdownComponents as any}>
                {(() => {
                  // Process BBCode tags inline
                  if (!part.content) return '';

                  let processedContent = part.content;

                  // Process [b]bold[/b] tags to markdown **bold**
                  processedContent = processedContent.replace(/\[b\](.*?)\[\/b\]/g, '**$1**');

                  // Process [i]italic[/i] tags to markdown *italic*
                  processedContent = processedContent.replace(/\[i\](.*?)\[\/i\]/g, '*$1*');

                  // Process [u]underline[/u] tags
                  processedContent = processedContent.replace(/\[u\](.*?)\[\/u\]/g, '<u>$1</u>');

                  // Process [url=http://example.com]link text[/url] tags
                  processedContent = processedContent.replace(/\[url=(.*?)\](.*?)\[\/url\]/g, '[$2]($1)');

                  // Convert standalone URLs to markdown links
                  processedContent = processedContent.replace(/(?<!\]\(|\))(https?:\/\/[^\s<>]+)(?![^\(]*\))/g, '[$1]($1)');

                  // Process standalone email addresses to markdown links
                  processedContent = processedContent.replace(/\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g, '[$1](mailto:$1)');

                  // Format and link US phone numbers
                  const phoneRegex = /(?<!\w)(?:\+1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}(?!\w)/g;
                  processedContent = processedContent.replace(phoneRegex, (m) => {
                    const digits = (m.match(/\d/g) || []).join('');
                    let d = digits;
                    if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
                    if (d.length !== 10) return m;
                    const formatted = `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                    const tel = digits.startsWith('1') ? digits : `1${d}`;
                    return `[${formatted}](tel:${tel})`;
                  });

                  return processedContent;
                })()}
              </ReactMarkdown>
            </div>
          );
        }
      })}
    </div>
  );
};

export type FeedbackControlsProps = {
  rating: number;
  onRatingChange: (rating: number) => void;
  comment: string;
  onCommentChange: (value: string) => void;
  onSubmit: () => void;
  existingFeedback?: { rating: number; comment?: string | null };
  isSubmitting: boolean;
};

export const ratingScale = Array.from({ length: 10 }, (_, index) => index + 1);

export const FeedbackControls: React.FC<FeedbackControlsProps> = ({
  rating,
  onRatingChange,
  comment,
  onCommentChange,
  onSubmit,
  existingFeedback,
  isSubmitting,
}) => {
  // AR-210 (TC-34.5): ties the ten score buttons to their visible label.
  const ratingLabelId = React.useId();
  // Once the user has rated this message (persisted in `existingFeedback`),
  // collapse the widget into a "Thanks" confirmation with an Edit affordance
  // so the same response doesn't keep prompting for re-rating.
  const [isEditing, setIsEditing] = useState(!existingFeedback);

  // Auto-collapse whenever `existingFeedback` gains a value or its contents
  // change (covers first-time rates and edits that change rating/comment).
  useEffect(() => {
    if (existingFeedback) {
      setIsEditing(false);
    }
  }, [existingFeedback?.rating, existingFeedback?.comment]);

  // Also auto-collapse on every submit completion (handles the edge case of
  // re-submitting identical rating/comment, where the effect above wouldn't
  // fire). `isSubmitting` transitions true → false after the mutation settles.
  const prevSubmittingRef = useRef(isSubmitting);
  useEffect(() => {
    const wasSubmitting = prevSubmittingRef.current;
    prevSubmittingRef.current = isSubmitting;
    if (wasSubmitting && !isSubmitting && existingFeedback) {
      setIsEditing(false);
    }
  }, [isSubmitting, existingFeedback]);

  if (!isEditing && existingFeedback) {
    return (
      <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden />
          <span>
            Thanks for your feedback — {existingFeedback.rating}/10
          </span>
        </div>
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="text-xs font-medium text-orange-600 hover:text-orange-700 hover:underline"
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span id={ratingLabelId} className="text-sm font-medium text-gray-700">
            Rate this response
          </span>
          <div className="flex items-center gap-3">
            {existingFeedback && (
              <span className="text-xs text-gray-500">
                Last rating: {existingFeedback.rating}/10
              </span>
            )}
            {existingFeedback && (
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                disabled={isSubmitting}
                aria-label="Close feedback editor"
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-labelledby={ratingLabelId}>
          {ratingScale.map((score) => (
            <button
              key={score}
              type="button"
              onClick={() => onRatingChange(score)}
              disabled={isSubmitting}
              aria-pressed={rating === score}
              aria-label={`${score} out of 10`}
              className={`h-8 w-8 rounded-full border text-sm font-medium transition ${
                rating === score
                  ? 'bg-orange-600 text-white border-orange-700'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {score}
            </button>
          ))}
        </div>
        <Textarea
          value={comment}
          onChange={(event) => onCommentChange(event.target.value)}
          placeholder="Optional: Share what worked or what needs improvement"
          aria-label="Feedback comment (optional)"
          disabled={isSubmitting}
          className="min-h-[80px]"
        />
        {/* AR-316: both rows wrap. Editing existing feedback adds Cancel, and
            the pair needs 220px against the 208px of content width a 320px
            phone leaves here — with an `overflow-hidden` ancestor that clipped
            75px of the Submit button and made it unclickable. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-gray-500">
            Ratings help us tune routing and tooling decisions.
          </span>
          <div className="ml-0 flex flex-wrap justify-end gap-2 sm:ml-3 sm:items-center">
            {existingFeedback && (
              <Button
                variant="ghost"
                onClick={() => setIsEditing(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={onSubmit}
              disabled={isSubmitting || rating < 1}
            >
              {isSubmitting ? 'Saving...' : 'Submit feedback'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export type ReplayDetailsPanelProps = {
  replay?: ReplayRecord | null;
  isExpanded: boolean;
  isLoading: boolean;
  onToggle: () => void;
};

export const ReplayDetailsPanel: React.FC<ReplayDetailsPanelProps> = ({
  replay,
  isExpanded,
  isLoading,
  onToggle,
}) => {
  if (isLoading && !replay) {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-orange-500" />
        Loading replay details…
      </div>
    );
  }

  if (!replay) {
    return (
      <p className="mt-2 text-xs text-gray-500">
        Replay details unavailable for this response.
      </p>
    );
  }

  const context = replay.injectedContext ?? null;
  const plan = replay.orchestrationPlan ?? null;
  const planSteps = Array.isArray(plan?.steps) ? plan.steps! : [];
  const toolExecutions = Array.isArray(replay.toolExecutions)
    ? replay.toolExecutions
    : [];
  const hasPlanOrTools = planSteps.length > 0 || toolExecutions.length > 0;
  const isV2Fallback = replay.degradedReason === 'V2_FALLBACK';
  const degradeMessages: Record<string, string> = {
    V2_FALLBACK:
      'Agentic orchestration failed before execution and the system avoided sending a duplicate response.',
    planner_degraded:
      'Planner service reported a degraded experience for this request.',
    flag_off: 'Agentic orchestration was disabled by feature flag at runtime.',
  };
  const contextEntries = context
    ? [
        context.header ? { label: 'Summary', value: context.header } : null,
        context.scope ? { label: 'Scope', value: context.scope } : null,
        context.projectName
          ? { label: 'Project', value: context.projectName }
          : null,
        context.projectType
          ? { label: 'Project type', value: context.projectType }
          : null,
        typeof context.chatProjectId === 'number'
          ? { label: 'Project ID', value: context.chatProjectId }
          : null,
        typeof context.hasGlobalCards === 'boolean'
          ? {
              label: 'Global cards',
              value: context.hasGlobalCards ? 'Yes' : 'No',
            }
          : null,
      ].filter(Boolean)
    : [];

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 text-sm font-medium text-orange-600 hover:text-orange-700"
      >
        <Eye className="h-4 w-4" />
        {isExpanded ? 'Hide details' : 'View details'}
        {isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>
      {isExpanded && (
        <div className="mt-3 space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          {isV2Fallback && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4" />
              <div>
                <p className="font-medium">Agentic V2 fallback</p>
                <p className="mt-0.5 text-sm">
                  {replay.fallbackReason ||
                    'Agentic orchestration returned without executing a plan.'}
                </p>
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase text-gray-500">
              Sanitized request
            </p>
            <p className="mt-1 whitespace-pre-line rounded bg-white px-3 py-2 font-mono text-xs text-gray-800">
              {replay.sanitizedUserInput || 'Not captured'}
            </p>
          </div>

          {contextEntries.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500">
                Context summary
              </p>
              <dl className="mt-1 grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
                {contextEntries.map((entry) => (
                  <div key={`${entry!.label}`} className="flex gap-2">
                    <dt className="text-gray-500">{entry!.label}:</dt>
                    <dd className="text-gray-800">{entry!.value as string}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase text-gray-500">
              Planner &amp; provider
            </p>
            <div className="mt-1 flex flex-wrap gap-3 text-sm">
              <span>
                <span className="text-gray-500">Provider:</span>{' '}
                <span className="text-gray-900">
                  {replay.plannerProvider || 'Unknown'}
                </span>
              </span>
              <span>
                <span className="text-gray-500">Model:</span>{' '}
                <span className="text-gray-900">
                  {replay.plannerModel || 'Unknown'}
                </span>
              </span>
              <span>
                <span className="text-gray-500">Latency:</span>{' '}
                <span className="text-gray-900">
                  {typeof replay.plannerLatencyMs === 'number'
                    ? `${replay.plannerLatencyMs}ms`
                    : 'n/a'}
                </span>
              </span>
              <span>
                <span className="text-gray-500">Attempt:</span>{' '}
                <span className="text-gray-900">
                  {typeof replay.plannerAttempt === 'number'
                    ? replay.plannerAttempt
                    : 'n/a'}
                </span>
              </span>
              <span>
                <span className="text-gray-500">Degraded:</span>{' '}
                <span className="text-gray-900">
                  {replay.plannerDegraded ? 'Yes' : 'No'}
                </span>
              </span>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase text-gray-500">
              Plan &amp; tools
            </p>
            {plan?.reasoning && (
              <p className="mt-1 rounded border border-gray-200 bg-white px-3 py-2 text-gray-800">
                {plan.reasoning}
              </p>
            )}
            {hasPlanOrTools ? (
              <div className="mt-2 space-y-3">
                {planSteps.length > 0 && (
                  <div className="space-y-2">
                    {planSteps.map((step, index) => (
                      <div
                        key={step.id ?? `plan-step-${index}`}
                        className="rounded border border-gray-200 bg-white px-3 py-2"
                      >
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>
                            Step {index + 1}:{' '}
                            <span className="text-gray-700">
                              {step.id || `step-${index + 1}`}
                            </span>
                          </span>
                          {step.parallel && <span>Parallel</span>}
                        </div>
                        <div className="mt-1 text-sm text-gray-800">
                          Tools:{' '}
                          {Array.isArray(step.tools) && step.tools.length > 0
                            ? step.tools
                                .map((tool) => tool.tool || 'Unknown tool')
                                .join(', ')
                            : 'Not specified'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {toolExecutions.length > 0 && (
                  <div className="space-y-2">
                    {toolExecutions.map((execution, index) => (
                      <div
                        key={`${execution.toolName}-${index}`}
                        className="rounded border border-gray-200 bg-white px-3 py-2"
                      >
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-900">
                            {execution.toolName || 'Tool'}
                          </span>
                          <Badge
                            className={`${
                              execution.status === 'success'
                                ? 'bg-green-100 text-green-800'
                                : execution.status === 'failure' ||
                                    execution.status === 'failed'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {execution.status}
                          </Badge>
                        </div>
                        {execution.resultPreview && (
                          <p className="mt-1 text-sm text-gray-700">
                            {execution.resultPreview}
                          </p>
                        )}
                        {execution.error && (
                          <p className="mt-1 text-sm text-red-600">
                            {execution.error}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                Planner metadata available but no plan/tool details were recorded.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-2 text-sm text-gray-600 md:grid-cols-2">
            <div>
              <span className="text-gray-500">Fallback reason:</span>{' '}
              <span className="text-gray-900">
                {replay.fallbackReason ?? 'None'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Request ID:</span>{' '}
              <span className="text-gray-900">
                {replay.requestId ?? 'n/a'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Decision:</span>{' '}
              <span className="text-gray-900">
                {(replay.decision as any)?.reason || 'n/a'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Unsplash grid (separate container) for assistant messages
export const UnsplashGrid: React.FC<{ meta: any }> = ({ meta }) => {
  const images = meta?.unsplash?.images || [];
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const openAt = (i: number) => { setIndex(i); setOpen(true); };
  const close = () => setOpen(false);
  const next = () => setIndex((i) => Math.min(i + 1, images.length - 1));
  const prev = () => setIndex((i) => Math.max(i - 1, 0));

  const current = images[index] || {};

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index]);

  const downloadImage = () => {
    try {
      const url = current.full_url || current.download_url || current.thumbnail_url;
      if (!url) return;

      const a = document.createElement('a');
      a.href = url;
      const urlObj = new URL(url);
      let base = urlObj.pathname.split('/').pop() || (current.id || 'image');
      if (!/\.(jpg|jpeg|png|webp)$/i.test(base)) base += '.jpg';
      a.download = base;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { console.error('Download failed:', e); }
  };

  const copyToClipboard = async () => {
    try {
      const url = current.full_url || current.thumbnail_url;
      const resp = await fetch(url, { mode: 'cors' });
      const blob = await resp.blob();
      // @ts-ignore
      await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
    } catch (e) {
      console.warn('Image clipboard copy failed, copying URL instead');
      try { await navigator.clipboard.writeText(current.full_url || ''); } catch {}
    }
  };

  return (
    <div className="mt-3">
      <div className="text-xs text-gray-600 mb-1">
        Visual Inspiration · {meta.unsplash.count} images for "{meta.unsplash.searchTerms}"
      </div>
      <div className="unsplash-image-grid">
        {images.map((img: any, i: number) => (
          <div key={img.id} className="unsplash-image-item" onClick={() => openAt(i)} title={img.description}>
            <img src={img.thumbnail_url} alt={img.description} className="unsplash-thumbnail" />
            <div className="unsplash-attribution">
              {img.photographer?.name && (
                <><a href={img.photographer.profile_url || undefined} target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()}>{img.photographer.name}</a> | </>
              )}<a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()}>Unsplash</a>
            </div>
            {/* meta hidden by CSS */}
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center" onClick={close}>
          <div
            className="relative max-w-[90vw] p-2 bg-white rounded-lg"
            style={{ minHeight: '300px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/90 text-gray-800 hover:bg-white shadow-lg border border-gray-200 flex items-center justify-center transition-colors"
              title="Close"
            >
              ✕
            </button>
            <div className="flex-1 w-full flex items-center justify-center overflow-hidden">
              <img src={current.full_url} alt={current.description} style={{ maxWidth: '85vw', maxHeight: '100%', height: 'auto', width: 'auto', objectFit: 'contain' }} />
            </div>
            <div className="flex items-center justify-between w-full px-2 py-2">
              <div className="text-xs text-gray-600">{index + 1} / {images.length}</div>
              <div className="flex items-center gap-2">
                <button onClick={prev} disabled={index === 0} className="p-2 bg-gray-100 rounded disabled:opacity-50" title="Previous">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={next} disabled={index === images.length - 1} className="p-2 bg-gray-100 rounded disabled:opacity-50" title="Next">
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button onClick={downloadImage} className="p-2 bg-gray-100 rounded" title="Download">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Version badge/dropdown component
export const VersionBadge: React.FC<{ message: any; onRestore: (versionIndex: number) => void }> = ({ message, onRestore }) => {
  const versions = Array.isArray(message?.metadata?.versions) ? message.metadata.versions : [];
  const currentIndex = typeof message?.metadata?.currentVersionIndex === 'number' ? message.metadata.currentVersionIndex : versions.length;
  // Show selector whenever at least one rewrite exists, even when viewing original
  if (versions.length === 0) return null;

  return (
    <div className="absolute top-0 right-0 mt-1 mr-1 text-xs text-gray-500">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="px-1 py-0.5 rounded bg-transparent hover:bg-gray-100 flex items-center gap-1 text-orange-600">
            v{currentIndex + 1}
            <ChevronDown className="w-3 h-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[160px]">
          {[...Array(versions.length + 1)].map((_, idx) => (
            <DropdownMenuItem key={idx} onClick={() => onRestore(idx)}>
              {idx === 0 ? 'v1 (original)' : `v${idx + 1}`}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
